import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DateInput } from "./ui/date-input";
import { isoDateIST } from "../lib/utils";
import { toast } from "sonner";

export const PAY_MODES = [
  { v: "cash", l: "Cash", ref: "Cash voucher / receipt no. (optional)" },
  { v: "upi", l: "UPI", ref: "UPI transaction ID" },
  { v: "bank_transfer", l: "Bank transfer (IMPS)", ref: "UTR / reference no." },
  { v: "neft", l: "NEFT", ref: "UTR no." },
  { v: "cheque", l: "Cheque", ref: "Cheque no. & bank" },
];
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

function groupByEmployee(claims) {
  const g = {};
  (claims || []).forEach((c) => { (g[c.employee_id || c.employee_name] ||= { name: c.employee_name, claims: [] }).claims.push(c); });
  return Object.entries(g).map(([key, v]) => ({ key, ...v, total: v.claims.reduce((s, c) => s + Number(c.total_amount || 0), 0) }));
}

function EmployeePayment({ grp, f, set }) {
  const mode = PAY_MODES.find((m) => m.v === f.mode);
  const t = slug(grp.name);
  return (
    <div className="rounded-md border border-zinc-200 p-3" data-testid={`exp-pay-group-${t}`}>
      <div className="flex justify-between text-sm font-semibold mb-1"><span>{grp.name}</span><span data-testid={`exp-pay-group-total-${t}`}>{inr(grp.total)}</span></div>
      <div className="text-xs text-zinc-500 mb-2">{grp.claims.map((c) => `${c.title} (${inr(c.total_amount)})`).join(" · ")}</div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[11px]">Paid by</Label>
          <Select value={f.mode} onValueChange={(v) => set("mode", v)}>
            <SelectTrigger className="h-9" data-testid={`exp-pay-mode-${t}`}><SelectValue/></SelectTrigger>
            <SelectContent>{PAY_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-[11px]">Payment date</Label><DateInput value={f.paid_on} onChange={(e) => set("paid_on", e.target.value)} data-testid={`exp-pay-date-${t}`}/></div>
        <div className="col-span-2"><Label className="text-[11px]">{mode?.ref}</Label><Input className="h-9" value={f.reference} onChange={(e) => set("reference", e.target.value)} data-testid={`exp-pay-reference-${t}`}/></div>
        <div><Label className="text-[11px]">Paid to</Label><Input className="h-9" value={f.paid_to} onChange={(e) => set("paid_to", e.target.value)} data-testid={`exp-pay-paid-to-${t}`}/></div>
        <div><Label className="text-[11px]">Notes</Label><Input className="h-9" value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="e.g. petty cash, site office" data-testid={`exp-pay-notes-${t}`}/></div>
      </div>
    </div>
  );
}

// claims: [{id,title,employee_id,employee_name,total_amount,status}] — one payment is recorded per employee
export function ExpensePaymentDialog({ claims, onClose, onDone }) {
  const groups = groupByEmployee(claims);
  const [forms, setForms] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const init = {};
    groupByEmployee(claims).forEach((g) => { init[g.key] = { mode: "cash", paid_on: isoDateIST(), reference: "", paid_to: g.name, notes: "" }; });
    setForms(init);
  }, [claims]);
  const open = !!claims?.length;
  const total = groups.reduce((s, g) => s + g.total, 0);
  const hasSubmitted = (claims || []).some((c) => c.status === "submitted");
  const save = async () => {
    setBusy(true);
    let done = 0;
    try {
      for (const g of groups) {
        await api.post("/expenses/record-payment", { ...forms[g.key], claim_ids: g.claims.map((c) => c.id), approve_submitted: hasSubmitted });
        done += 1;
      }
      toast.success(groups.length > 1 ? `${groups.length} payments recorded (one per employee)` : "Payment recorded");
      onDone();
    } catch (e) {
      toast.error(`${done ? `${done} payment(s) saved, then: ` : ""}${formatApiError(e?.response?.data?.detail)}`);
      if (done) onDone();
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="exp-pay-dialog">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
          <DialogDescription>Paid directly, not through payroll. Each employee's claims are combined into one payment.</DialogDescription>
        </DialogHeader>
        {hasSubmitted && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Claims still pending review will be approved when you save.</p>}
        <div className="space-y-3">
          {groups.map((g) => forms[g.key] && (
            <EmployeePayment key={g.key} grp={g} f={forms[g.key]} set={(k, v) => setForms((p) => ({ ...p, [g.key]: { ...p[g.key], [k]: v } }))}/>
          ))}
        </div>
        <div className="flex justify-between text-sm font-semibold"><span>Total ({groups.length} employee{groups.length > 1 ? "s" : ""})</span><span data-testid="exp-pay-total">{inr(total)}</span></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="exp-pay-save">{busy ? "Saving…" : groups.length > 1 ? `Save ${groups.length} payments` : "Save payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
